/* IFRS 18 and Cash Flow Fixes Card V2 · item A8 — management-defined performance measures (MPM).
   The editor on the financial statements page (tab "Performance measures" of IFRS.jsx) and the note view used inside the
   Notes tab (note 78). A measure: name, description, the P&L subtotal it starts from, the reconciling items (an FS line or a
   note code of the engagement — the amount is read from the current statements, the sign is chosen) and per item the income
   tax effect (an amount, or a rate in %) and the optional effect on non-controlling interests. Every figure comes from
   GET /api/fs/{id}/mpm (built from the current statements on each read); only the typed values are saved.
   EN + AR text here (IFRS.jsx and i18n.js are over the 1,500-line rule). */
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { asArray } from "@/lib/asArray";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDateTimeFirm } from "@/lib/dt";
import { askConfirm } from "@/lib/appDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { fmtNumber } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter

export const MPM_TEXT = {
  en: {
    tab: "Performance measures", title: "Management-defined performance measures",
    intro: "Define a measure management uses outside the statements. It is reconciled to the profit or loss subtotal it starts from, with every amount read from the current statements, and shown in the notes.",
    empty: "No measures defined. Without a measure there is no performance-measures note.",
    add: "Add a measure", edit: "Edit", del: "Delete", save: "Save measure", cancel: "Cancel",
    name: "Name", description: "Description", start: "Start subtotal", pickStart: "Choose a subtotal",
    items: "Reconciling items", addItem: "Add a reconciling item", removeItem: "Remove", pickItem: "Choose an FS line or note code",
    fsGroup: "Financial statement lines", noteGroup: "Note codes",
    sign: "Sign", signAdd: "+ Add", signDeduct: "− Deduct", amount: "Amount (current statements)",
    tax: "Income tax effect", taxNone: "No tax effect", taxRate: "At a rate (%)", taxAmount: "Amount entered",
    rate: "Rate %", taxCy: "Tax effect", taxPy: "Tax effect (prior year)",
    nci: "Effect on NCI (optional)", nciPy: "Effect on NCI, prior year (optional)",
    rateHint: "At a rate the tax effect is the item × rate with the opposite sign (tax on an amount added back is deducted).",
    measure: "Measure", item: "Item", effectNci: "Effect on NCI", total: "Total",
    notInStatements: "Not in the current statements (amount 0)", startUnavailable: "This subtotal is not on the current profit or loss statement.",
    locked: "The engagement is signed off — measures are read-only.", updated: "Last edited {when} by {who}",
    confirmDelete: "Delete the measure “{name}”? The performance-measures note changes at once.",
    required: "Enter the name, description, start subtotal and at least one reconciling item (each with an FS line or note code).",
    rateInvalid: "Enter the tax rate as a percentage from 0 to 100.", amountRequired: "Enter the tax effect amount, or choose a rate.",
    failed: "The measure could not be saved.", loadFailed: "The measures could not be loaded.", loading: "Loading…",
    saved: "Measure saved.", deleted: "Measure deleted.",
  },
  ar: {
    tab: "مقاييس الأداء", title: "مقاييس الأداء المحددة من الإدارة",
    intro: "عرّف مقياسًا تستخدمه الإدارة خارج القوائم المالية. تتم مطابقته مع المجموع الفرعي في قائمة الربح أو الخسارة الذي يبدأ منه، مع قراءة كل مبلغ من القوائم الحالية، ويظهر في الإيضاحات.",
    empty: "لا توجد مقاييس معرّفة. بدون مقياس لا يوجد إيضاح لمقاييس الأداء.",
    add: "إضافة مقياس", edit: "تعديل", del: "حذف", save: "حفظ المقياس", cancel: "إلغاء",
    name: "الاسم", description: "الوصف", start: "المجموع الفرعي للبداية", pickStart: "اختر مجموعًا فرعيًا",
    items: "البنود التسووية", addItem: "إضافة بند تسووي", removeItem: "إزالة", pickItem: "اختر بندًا من القوائم المالية أو رمز إيضاح",
    fsGroup: "بنود القوائم المالية", noteGroup: "رموز الإيضاحات",
    sign: "الإشارة", signAdd: "+ إضافة", signDeduct: "− خصم", amount: "المبلغ (القوائم الحالية)",
    tax: "أثر ضريبة الدخل", taxNone: "بلا أثر ضريبي", taxRate: "بنسبة (%)", taxAmount: "مبلغ مُدخل",
    rate: "النسبة %", taxCy: "الأثر الضريبي", taxPy: "الأثر الضريبي (السنة السابقة)",
    nci: "الأثر على الحصص غير المسيطرة (اختياري)", nciPy: "الأثر على الحصص غير المسيطرة، السنة السابقة (اختياري)",
    rateHint: "عند اختيار نسبة يكون الأثر الضريبي هو البند × النسبة بإشارة معاكسة (الضريبة على مبلغ مُضاف تُخصم).",
    measure: "المقياس", item: "البند", effectNci: "الأثر على الحصص غير المسيطرة", total: "الإجمالي",
    notInStatements: "غير موجود في القوائم الحالية (المبلغ 0)", startUnavailable: "هذا المجموع الفرعي غير موجود في قائمة الربح أو الخسارة الحالية.",
    locked: "تم اعتماد الارتباط — المقاييس للقراءة فقط.", updated: "آخر تعديل {when} بواسطة {who}",
    confirmDelete: "حذف المقياس «{name}»؟ يتغير إيضاح مقاييس الأداء فورًا.",
    required: "أدخل الاسم والوصف والمجموع الفرعي للبداية وبندًا تسوويًا واحدًا على الأقل (لكل بند بند من القوائم أو رمز إيضاح).",
    rateInvalid: "أدخل نسبة الضريبة كنسبة مئوية من 0 إلى 100.", amountRequired: "أدخل مبلغ أثر الضريبة، أو اختر نسبة.",
    failed: "تعذّر حفظ المقياس.", loadFailed: "تعذّر تحميل المقاييس.", loading: "جارٍ التحميل…",
    saved: "تم حفظ المقياس.", deleted: "تم حذف المقياس.",
  },
};
export const mpmText = (lang) => MPM_TEXT[lang === "ar" ? "ar" : "en"];

const fmt = (n) => (n === null || n === undefined || n === "" ? "" :
  fmtNumber((Math.abs(Number(n)) < 0.005 ? 0 : Number(n))));
const blankItem = () => ({ key: "", direction: "add", tax_mode: "none", tax_rate: "", tax_amount: "", tax_amount_py: "", nci_amount: "", nci_amount_py: "" });
const optKey = (o) => `${o.source}|${o.code}`;
const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const selCls = "h-8 w-full rounded-md border border-input bg-background px-2 text-xs";

/** Client-side check before sending; returns the message key or "". */
export function validateMeasure(f) {
  const blank = (v) => !String(v ?? "").trim();
  if (blank(f.name) || blank(f.description) || blank(f.start_subtotal) || !f.items.length || f.items.some((it) => blank(it.key))) return "required";
  for (const it of f.items) {
    if (it.tax_mode === "rate") {
      const r = Number(it.tax_rate);
      if (blank(it.tax_rate) || Number.isNaN(r) || r < 0 || r > 100) return "rateInvalid";
    }
    if (it.tax_mode === "amount" && (blank(it.tax_amount) || Number.isNaN(Number(it.tax_amount)))) return "amountRequired";
  }
  return "";
}

/** One measure's reconciliation (screen and note 78). */
export function MpmReconTable({ rec, hasPrior, fy, lang }) {
  const colW = useResizableColumns({ storageKey: "mpm-panel", defaults: [100, 130, 100, 100, 100, 100], mins: [60, 90, 60, 60, 60, 60] });   // Step 11d · add-on
  const L = mpmText(lang);
  const sub = lang === "ar" ? (rec.start_label_ar || rec.start_label) : rec.start_label;
  const pyLabel = fy && /^\d+$/.test(String(fy)) ? `FY${Number(fy) - 1}` : "";
  const taxCell = (it) => (it.tax_cy === null || it.tax_cy === undefined ? "" : fmt(it.tax_cy) + (it.tax_rate !== null && it.tax_rate !== undefined ? ` (${it.tax_rate}%)` : ""));
  return (
    <ResizableTable cols={colW} wrap className="w-full text-xs" data-testid={`mpm-recon-${rec.id || "x"}`}>
      <thead>
        <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
          <ResizableTh index={0} className="py-1 pe-2 text-start">{L.item}</ResizableTh>
          <ResizableTh index={1} className="py-1 pe-2 text-end">{fy ? `FY${fy}` : ""}</ResizableTh>
          {hasPrior && <ResizableTh index={2} className="py-1 pe-2 text-end">{pyLabel}</ResizableTh>}
          <ResizableTh index={3} className="py-1 pe-2 text-end">{L.tax}</ResizableTh>
          {hasPrior && <ResizableTh index={4} className="py-1 pe-2 text-end">{L.taxPy}</ResizableTh>}
          <ResizableTh index={5} className="py-1 pe-2 text-end">{L.effectNci}</ResizableTh>
        </tr>
      </thead>
      <tbody>
        <tr className="border-t border-slate-200/60">
          <td className="py-1 pe-2">{sub || rec.start_subtotal}{!rec.start_available && <span className="ms-1 text-rose-700">({L.startUnavailable})</span>}</td>
          <td className="py-1 pe-2 text-end num-cell">{fmt(rec.start_cy)}</td>
          {hasPrior && <td className="py-1 pe-2 text-end num-cell">{fmt(rec.start_py)}</td>}
          <td /><td />{hasPrior && <td />}
        </tr>
        {asArray(rec.items).map((it, i) => (
          <tr key={`${it.code}-${i}`} className="border-t border-slate-200/60" data-testid="mpm-recon-item">
            <td className="py-1 pe-2">
              <span className="me-1 font-semibold">{it.sign}</span>{(lang === "ar" && it.title_ar) || it.title} <span className="font-mono text-[10px] text-muted-foreground" dir="ltr">{it.code}</span>
              {!it.in_statements && <span className="ms-1 text-[10px] text-amber-700">{L.notInStatements}</span>}
            </td>
            <td className="py-1 pe-2 text-end num-cell">{fmt(it.effect_cy)}</td>
            {hasPrior && <td className="py-1 pe-2 text-end num-cell">{fmt(it.effect_py)}</td>}
            <td className="py-1 pe-2 text-end num-cell">{taxCell(it)}</td>
            {hasPrior && <td className="py-1 pe-2 text-end num-cell">{fmt(it.tax_py)}</td>}
            <td className="py-1 pe-2 text-end num-cell">{fmt(it.nci_cy)}{hasPrior && it.nci_py !== null && it.nci_py !== undefined ? ` / ${fmt(it.nci_py)}` : ""}</td>
          </tr>
        ))}
        <tr className="border-t-2 border-slate-300 font-semibold" data-testid="mpm-recon-total">
          <td className="py-1 pe-2">{rec.name}</td>
          <td className="py-1 pe-2 text-end num-cell">{fmt(rec.measure_cy)}</td>
          {hasPrior && <td className="py-1 pe-2 text-end num-cell">{fmt(rec.measure_py)}</td>}
          <td className="py-1 pe-2 text-end num-cell">{rec.any_tax ? fmt(rec.tax_total_cy) : ""}</td>
          {hasPrior && <td className="py-1 pe-2 text-end num-cell">{rec.any_tax ? fmt(rec.tax_total_py) : ""}</td>}
          <td className="py-1 pe-2 text-end num-cell">{rec.any_nci ? fmt(rec.nci_total_cy) : ""}</td>
        </tr>
      </tbody>
    </ResizableTable>
  );
}

/** Note 78 inside the Notes tab: per measure the description and the reconciliation. */
export function MpmNoteView({ mpm, fy }) {
  const { lang } = useI18n();
  return (
    <div className="mt-2 space-y-3" data-testid="mpm-note">
      {asArray(mpm?.measures).map((m) => (
        <div key={m.id}>
          <div className="text-xs font-semibold">{m.name}</div>
          <p className="mb-1 whitespace-pre-wrap text-[11px] text-muted-foreground">{m.description}</p>
          <MpmReconTable rec={m} hasPrior={!!mpm.has_prior} fy={fy} lang={lang} />
        </div>
      ))}
    </div>
  );
}

function MeasureForm({ data, initial, onSaved, onCancel, engId }) {
  const { lang } = useI18n();
  const L = mpmText(lang);
  const [f, setF] = useState(initial);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const opts = asArray(data.items);
  const byKey = Object.fromEntries(opts.map((o) => [optKey(o), o]));
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const setItem = (i, k, v) => setF((x) => ({ ...x, items: x.items.map((it, j) => {
    if (j !== i) return it;
    const next = { ...it, [k]: v };
    if (k === "key" && byKey[v]) next.direction = byKey[v].expense ? "add" : "deduct";   // the usual sign: add back an expense
    return next;
  }) }));
  const bad = (cond) => (err && cond ? "border-rose-500" : "");
  const submit = async () => {
    const e = validateMeasure(f);
    if (e) { setErr(L[e]); return; }
    setBusy(true); setErr("");
    const body = {
      name: f.name.trim(), description: f.description.trim(), start_subtotal: f.start_subtotal,
      items: f.items.map((it) => {
        const [source, ...rest] = it.key.split("|");
        return { source, code: rest.join("|"), direction: it.direction, tax_mode: it.tax_mode,
                 tax_rate: it.tax_mode === "rate" ? Number(it.tax_rate) : null,
                 tax_amount: it.tax_mode === "amount" ? numOrNull(it.tax_amount) : null,
                 tax_amount_py: it.tax_mode === "amount" ? numOrNull(it.tax_amount_py) : null,
                 nci_amount: numOrNull(it.nci_amount), nci_amount_py: numOrNull(it.nci_amount_py) };
      }),
    };
    try {
      if (f.id) await api.put(`/fs/${engId}/mpm/${f.id}`, body, { _silent: true });
      else await api.post(`/fs/${engId}/mpm`, body, { _silent: true });
      onSaved?.();
    } catch (ex) {
      const d = ex?.response?.data || {};
      setErr((lang === "ar" ? d.detail_ar : d.detail) || L.failed);
    } finally { setBusy(false); }
  };
  const fsOpts = opts.filter((o) => o.source === "fs");
  const noteOpts = opts.filter((o) => o.source === "note");
  const optLabel = (o) => `${o.title} (${o.code}) · ${fmt(o.cy)}`;
  return (
    <div className="space-y-3 rounded border border-sky-200 bg-sky-50/40 p-3 text-xs dark:border-sky-900 dark:bg-sky-950/20" data-testid="mpm-form">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <div>
          <Label htmlFor="mpm-name">{L.name} <span className="text-rose-700">*</span></Label>
          <Input id="mpm-name" value={f.name} onChange={set("name")} className={`h-8 ${bad(!f.name.trim())}`}
                 aria-invalid={!!err && !f.name.trim()} data-testid="mpm-name" />
        </div>
        <div>
          <Label htmlFor="mpm-start">{L.start} <span className="text-rose-700">*</span></Label>
          <select id="mpm-start" value={f.start_subtotal} onChange={set("start_subtotal")} className={`${selCls} ${bad(!f.start_subtotal)}`}
                  aria-invalid={!!err && !f.start_subtotal} data-testid="mpm-start">
            <option value="">{L.pickStart}</option>
            {asArray(data.subtotals).map((s) => (
              <option key={s.key} value={s.key}>{(lang === "ar" ? s.label_ar : s.label)} · {fmt(s.cy)}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="mpm-description">{L.description} <span className="text-rose-700">*</span></Label>
          <Textarea id="mpm-description" value={f.description} onChange={set("description")} rows={3}
                    className={bad(!f.description.trim())} aria-invalid={!!err && !f.description.trim()} data-testid="mpm-description" />
        </div>
      </div>
      <div>
        <div className="mb-1 font-medium">{L.items} <span className="text-rose-700">*</span></div>
        {f.items.map((it, i) => {
          const o = byKey[it.key];
          return (
            <div key={i} className="mb-2 grid grid-cols-1 gap-2 rounded border border-slate-200 bg-background p-2 md:grid-cols-6 dark:border-slate-800" data-testid={`mpm-item-${i}`}>
              <div className="md:col-span-3">
                <Label htmlFor={`mpm-item-${i}-key`}>{L.item}</Label>
                <select id={`mpm-item-${i}-key`} value={it.key} onChange={(e) => setItem(i, "key", e.target.value)}
                        className={`${selCls} ${bad(!it.key)}`} aria-invalid={!!err && !it.key} data-testid={`mpm-item-${i}-key`}>
                  <option value="">{L.pickItem}</option>
                  {fsOpts.length > 0 && <optgroup label={L.fsGroup}>{fsOpts.map((x) => <option key={optKey(x)} value={optKey(x)}>{optLabel(x)}</option>)}</optgroup>}
                  {noteOpts.length > 0 && <optgroup label={L.noteGroup}>{noteOpts.map((x) => <option key={optKey(x)} value={optKey(x)}>{optLabel(x)}</option>)}</optgroup>}
                </select>
              </div>
              <div>
                <Label htmlFor={`mpm-item-${i}-dir`}>{L.sign}</Label>
                <select id={`mpm-item-${i}-dir`} value={it.direction} onChange={(e) => setItem(i, "direction", e.target.value)} className={selCls}
                        data-testid={`mpm-item-${i}-dir`}>
                  <option value="add">{L.signAdd}</option>
                  <option value="deduct">{L.signDeduct}</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <div className="text-[11px] text-muted-foreground">{L.amount}</div>
                <div className="num-cell font-medium" data-testid={`mpm-item-${i}-amount`}>
                  {o ? `${it.direction === "deduct" ? "−" : "+"} ${fmt(o.cy)}` : ""}
                </div>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor={`mpm-item-${i}-tax`}>{L.tax}</Label>
                <select id={`mpm-item-${i}-tax`} value={it.tax_mode} onChange={(e) => setItem(i, "tax_mode", e.target.value)} className={selCls}
                        data-testid={`mpm-item-${i}-tax`}>
                  <option value="none">{L.taxNone}</option>
                  <option value="rate">{L.taxRate}</option>
                  <option value="amount">{L.taxAmount}</option>
                </select>
              </div>
              {it.tax_mode === "rate" && (
                <div>
                  <Label htmlFor={`mpm-item-${i}-rate`}>{L.rate}</Label>
                  <Input id={`mpm-item-${i}-rate`} type="number" min="0" max="100" step="0.01" dir="ltr" value={it.tax_rate}
                         onChange={(e) => setItem(i, "tax_rate", e.target.value)} className="h-8" data-testid={`mpm-item-${i}-rate`} />
                </div>
              )}
              {it.tax_mode === "amount" && (
                <>
                  <div>
                    <Label htmlFor={`mpm-item-${i}-taxamt`}>{L.taxCy}</Label>
                    <Input id={`mpm-item-${i}-taxamt`} type="number" step="0.01" dir="ltr" value={it.tax_amount}
                           onChange={(e) => setItem(i, "tax_amount", e.target.value)} className="h-8" data-testid={`mpm-item-${i}-taxamt`} />
                  </div>
                  {data.has_prior && (
                    <div>
                      <Label htmlFor={`mpm-item-${i}-taxamtpy`}>{L.taxPy}</Label>
                      <Input id={`mpm-item-${i}-taxamtpy`} type="number" step="0.01" dir="ltr" value={it.tax_amount_py}
                             onChange={(e) => setItem(i, "tax_amount_py", e.target.value)} className="h-8" data-testid={`mpm-item-${i}-taxamtpy`} />
                    </div>
                  )}
                </>
              )}
              <div>
                <Label htmlFor={`mpm-item-${i}-nci`}>{L.nci}</Label>
                <Input id={`mpm-item-${i}-nci`} type="number" step="0.01" dir="ltr" value={it.nci_amount}
                       onChange={(e) => setItem(i, "nci_amount", e.target.value)} className="h-8" data-testid={`mpm-item-${i}-nci`} />
              </div>
              {data.has_prior && (
                <div>
                  <Label htmlFor={`mpm-item-${i}-ncipy`}>{L.nciPy}</Label>
                  <Input id={`mpm-item-${i}-ncipy`} type="number" step="0.01" dir="ltr" value={it.nci_amount_py}
                         onChange={(e) => setItem(i, "nci_amount_py", e.target.value)} className="h-8" data-testid={`mpm-item-${i}-ncipy`} />
                </div>
              )}
              <div className="flex items-end">
                <Button size="sm" variant="outline" onClick={() => setF((x) => ({ ...x, items: x.items.filter((_, j) => j !== i) }))}
                        data-testid={`mpm-item-${i}-remove`}>{L.removeItem}</Button>
              </div>
            </div>
          );
        })}
        <Button size="sm" variant="outline" onClick={() => setF((x) => ({ ...x, items: [...x.items, blankItem()] }))} data-testid="mpm-add-item">
          {L.addItem}
        </Button>
        <p className="mt-1 text-[11px] text-muted-foreground">{L.rateHint}</p>
      </div>
      {err && <div className="text-rose-700" role="alert" data-testid="mpm-error">{err}</div>}
      <div className="flex gap-2">
        <Button size="sm" disabled={busy} onClick={submit} data-testid="mpm-save">{L.save}</Button>
        <Button size="sm" variant="outline" onClick={() => onCancel?.()} data-testid="mpm-cancel">{L.cancel}</Button>
      </div>
    </div>
  );
}

const toForm = (m) => ({
  id: m.id, name: m.name || "", description: m.description || "", start_subtotal: m.start_subtotal || "",
  items: asArray(m.items).map((it) => ({
    key: `${it.source}|${it.code}`, direction: it.direction || "add", tax_mode: it.tax_mode || "none",
    tax_rate: it.tax_rate ?? "", tax_amount: it.tax_amount ?? "", tax_amount_py: it.tax_amount_py ?? "",
    nci_amount: it.nci_amount ?? "", nci_amount_py: it.nci_amount_py ?? "",
  })),
});

export default function MpmPanel({ engagement }) {
  const { lang } = useI18n();
  const L = mpmText(lang);
  const engId = engagement?.id;
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    if (!engId) return;
    api.get(`/fs/${engId}/mpm`).then((r) => { setData(r.data); setLoadErr(""); })
      .catch((e) => setLoadErr((lang === "ar" ? e?.response?.data?.detail_ar : e?.response?.data?.detail) || L.loadFailed));
  }, [engId, lang]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const remove = async (m) => {
    const ok = await askConfirm(L.confirmDelete.replace("{name}", m.name));
    if (!ok) return;
    try { await api.delete(`/fs/${engId}/mpm/${m.id}`, { _silent: true }); load(); }
    catch (e) { setLoadErr((lang === "ar" ? e?.response?.data?.detail_ar : e?.response?.data?.detail) || L.failed); }
  };

  if (loadErr && !data) return <Card><CardContent className="p-6 text-sm text-rose-700" data-testid="mpm-load-error">{loadErr}</CardContent></Card>;
  if (!data) return <Card><CardContent className="p-6 text-sm text-muted-foreground">{L.loading}</CardContent></Card>;
  const measures = asArray(data.measures);
  return (
    <Card data-testid="mpm-panel">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="text-base">{L.title}</CardTitle>
        {!data.locked && !editing && (
          <Button size="sm" onClick={() => setEditing({ name: "", description: "", start_subtotal: "", items: [blankItem()] })} data-testid="mpm-add">
            {L.add}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <p className="text-xs text-muted-foreground">{L.intro}</p>
        {data.locked && <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="mpm-locked">{L.locked}</div>}
        {loadErr && <div className="text-xs text-rose-700" role="alert">{loadErr}</div>}
        {editing && (
          <MeasureForm data={data} engId={engId} initial={editing} onCancel={() => setEditing(null)}
                       onSaved={() => { setEditing(null); load(); }} />
        )}
        {!measures.length && !editing && <div className="text-xs text-muted-foreground" data-testid="mpm-empty">{L.empty}</div>}
        {measures.map((m) => (
          <div key={m.id} className="rounded border border-slate-200 p-3 dark:border-slate-800" data-testid={`mpm-measure-${m.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="font-medium">{m.name}</div>
                <p className="whitespace-pre-wrap text-xs text-muted-foreground">{m.description}</p>
                {m.updated_at && (
                  <div className="text-[10px] text-muted-foreground">
                    {L.updated.replace("{when}", fmtDateTimeFirm(m.updated_at)).replace("{who}", m.updated_by_name || "")}
                  </div>
                )}
              </div>
              {!data.locked && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditing(toForm(m))} data-testid={`mpm-edit-${m.id}`}>{L.edit}</Button>
                  <Button size="sm" variant="outline" onClick={() => remove(m)} data-testid={`mpm-delete-${m.id}`}>{L.del}</Button>
                </div>
              )}
            </div>
            <div className="mt-2">
              <MpmReconTable rec={m.reconciliation} hasPrior={!!data.has_prior} fy={data.financial_year} lang={lang} />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
